import {
    formatMinorAmount,
    toMinorUnits,
} from 'client/widget-components/ecom/stores/customAmount';
import CustomAmountState, {
    CustomAmountError,
    CustomAmountServerError,
    CUSTOM_AMOUNT_SDK_ERROR_PREFIX,
} from 'client/widget-components/ecom/stores/CustomAmountState';
import { EcomLocalizationService } from 'client/widget-components/ecom/types/EcomSDK';
import { ecommStr } from 'client/widgets/runtime-order-confirmation/useEcommStr';

export const CUSTOM_AMOUNT_PICKER_DATA_AUTO = 'custom-amount-picker';

const NAMESPACE = 'nativeEcommRuntime.';

const sdkErrorCode = (name: string) =>
    `${CUSTOM_AMOUNT_SDK_ERROR_PREFIX}${name}`;

// The SDK error each check of the picker matches, so the inline error reads
// exactly like the cart notice the SDK shows for the same amount.
const CLIENT_ERROR_CODES: Record<CustomAmountError, string> = {
    required: sdkErrorCode('required'),
    invalid: sdkErrorCode('invalid'),
    wholeAmount: sdkErrorCode('invalid_precision'),
    decimals: sdkErrorCode('invalid_precision'),
    belowMinimum: sdkErrorCode('below_minimum'),
    aboveMaximum: sdkErrorCode('above_maximum'),
};

/**
 * Rejections the shopper can fix in the picker. The SDK has already opened the
 * cart with its own notice; the inline message stays once the cart is closed.
 * The minimum it sends back includes the platform minimum, which the page
 * can't know.
 */
const INLINE_SDK_ERROR_CODES = [
    'required',
    'invalid',
    'invalid_precision',
    'below_minimum',
    'above_maximum',
    'not_suggested',
].map(sdkErrorCode);

const getLimit = (
    { config }: CustomAmountState,
    error: 'belowMinimum' | 'aboveMaximum'
) => {
    const [value, displayed] =
        error === 'belowMinimum'
            ? [config.minimum, config.displayedMinimum]
            : [config.maximum, config.displayedMaximum];
    if (displayed) {
        return displayed;
    }
    return value === null
        ? ''
        : formatMinorAmount(
              toMinorUnits(value, config.decimals),
              config.decimals,
              config.format
          );
};

/** The SDK error to show under the field, whether the picker or the backend found it. */
export function getCustomAmountNotice(
    customAmount: CustomAmountState
): CustomAmountServerError | null {
    const { serverError, error } = customAmount;
    if (serverError) {
        return INLINE_SDK_ERROR_CODES.includes(serverError.code)
            ? serverError
            : null;
    }
    if (!error) {
        return null;
    }
    const code = CLIENT_ERROR_CODES[error];
    return error === 'belowMinimum' || error === 'aboveMaximum'
        ? { code, params: { 0: getLimit(customAmount, error) } }
        : { code };
}

/**
 * Resolves the SDK's own `sdk_errors.custom_amount_*` phrases through its l10n
 * service, as the order confirmation does with `nativeEcommRuntime.*` phrases.
 * They load with the SDK, which the shopper's first interaction triggers.
 * Until then, or if the SDK never loads, the page's own copy of the phrases
 * stands in, as in `useEcommStr`.
 */
export function formatCustomAmountNotice(
    { code, params }: CustomAmountServerError,
    l10n: EcomLocalizationService | null
): string {
    const title = ecommStr(l10n, `${NAMESPACE}${code}.title`, params);
    const descriptionKey = `${NAMESPACE}${code}.description`;
    const description = ecommStr(l10n, descriptionKey, params);
    // The page's strings give back the key for a phrase they don't have. Like
    // the SDK's notices, a description whose {0} wasn't filled is dropped.
    return !description ||
        description === descriptionKey ||
        description.includes('{0}')
        ? title
        : `${title} ${description}`;
}
